//#region imports
import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AuthenticationService } from 'app/modules/authentication/services/authentication.service';
import { AppDataService } from 'app/modules/authentication/services/appdata.service';
import { Constant } from '../common/constant';
//#endregion imports

@Injectable({
  providedIn: 'root',
})
export class HttpErrorInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private toastr: ToastrService,
    private authService: AuthenticationService,
    private appDataService: AppDataService
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      // retry(1),
      catchError((error: HttpErrorResponse) => {
        let errorMessage = '';
        let displayText = '';
        // The loader counter is owned by LoaderInterceptor, which already
        // decrements it in its own catchError. Hiding here too double-counts
        // and drives the counter negative, after which showLoader() never
        // reaches 1 again and the spinner stops appearing for the rest of
        // the session.
        if (error.status === 401) {
          const message = error.error?.message || 'Your session has expired. Please log in again.';
          this.toastr.error(message, 'Session Expired');
          localStorage.clear();
          this.authService.logout().subscribe({
            complete: () => this.router.navigate(['/login']),
            error: () => this.router.navigate(['/login']),
          });
        } else if (error.status >= 400 && error.status < 500) {
          // Missing required custom fields are reported per-expense and can
          // easily run to twenty rows. A toast truncates that and disappears
          // before the user can act on it, so the component renders a
          // persistent dialog instead and we stay out of the way.
          if (this.isCustomFieldError(error.error)) {
            return throwError(error.error);
          }
          if (error.error && typeof error.error === 'object') {
            // Handle { success, message, data: { non_field_errors: [...] } } format
            const nonFieldErrors = error.error?.data?.non_field_errors;
            if (Array.isArray(nonFieldErrors) && nonFieldErrors.length > 0) {
              displayText = nonFieldErrors[0];
            } else if (error.error.message && typeof error.error.message === 'string') {
              displayText = error.error.message;
            } else {
              displayText = this.extractFirstString(error.error) || '';
            }
          } else if (typeof error.error === 'string') {
            displayText = error.error;
          }
          const isDuplicateGstin = displayText && (
            displayText.toLowerCase().includes('gstin') ||
            displayText.toLowerCase().includes('gst_no') ||
            displayText.toLowerCase().includes('unique set')
          );
          if (isDuplicateGstin) {
            displayText = 'GSTIN already exists. Please use a unique GSTIN.';
          }
          displayText = displayText || 'An error occurred. Please try again.';
          this.toastr.error(displayText, 'Error');
          // tslint:disable-next-line: no-string-literal
          errorMessage = error.error;
        } else if (error.status === 500) {
          let er_header='Internal Server Error'
          try {
            if (error.error?.is_failed){
              er_header='Unauthorized'
            }
            const errDetail: string = typeof error.error?.error === 'string' ? error.error.error : '';
            const isDuplicate = errDetail && (
              errDetail.toLowerCase().includes('unique') ||
              errDetail.toLowerCase().includes('duplicate') ||
              errDetail.toLowerCase().includes('already exists')
            );
            if (isDuplicate) {
              this.toastr.error('GST Number already exists. Please use a unique GST Number.', 'Duplicate Entry');
            } else {
              this.toastr.error(
                errDetail || 'There was issue at server side while processing your request',
                er_header
              );
            }
          } catch(e) {
            this.toastr.error(
              'There was issue at server side while processing your request',
              'Internal Server Error'
            );
          }

          //          this.router.navigate(['/error/accessright']);
        }
        return throwError(errorMessage);
      })
    );
  }

  // True for the missing-required-custom-field payloads the report endpoints
  // return. Two shapes exist: expense-level (a list of expenses, each with its
  // missing labels) and report-level (a DRF field-error dict).
  private isCustomFieldError(payload: any): boolean {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      return false;
    }
    if (Array.isArray(payload.expenses)) {
      return payload.expenses.some((e: any) => e && Array.isArray(e.missing_fields));
    }
    return (
      payload.custom_fields != null &&
      typeof payload.custom_fields === 'object' &&
      Object.keys(payload.custom_fields).length > 0
    );
  }

  // Walk nested DRF-style error payloads (dict → first entry value → array[0] → …)
  // and return the first string found. Prefers common message keys.
  private extractFirstString(value: any, depth = 0): string | null {
    if (depth > 6 || value == null) return null;
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) {
      for (const item of value) {
        const found = this.extractFirstString(item, depth + 1);
        if (found) return found;
      }
      return null;
    }
    if (typeof value === 'object') {
      const preferredKeys = ['detail', 'message', 'error', 'non_field_errors'];
      for (const key of preferredKeys) {
        if (key in value) {
          const found = this.extractFirstString(value[key], depth + 1);
          if (found) return found;
        }
      }
      for (const key of Object.keys(value)) {
        if (preferredKeys.includes(key)) continue;
        const found = this.extractFirstString(value[key], depth + 1);
        if (found) return found;
      }
    }
    return null;
  }
}
